type ChartSeries = {
  name: string;
  key: string;
  color: string;
  data: number[];
};

type TooltipParam = {
  axisValueLabel?: string;
  seriesName?: string;
  value?: number | string | null;
  marker?: string;
};

// Past this the tooltip covers the dashboard instead of explaining it.
const MAX_TOOLTIP_ROWS = 8;

// Story titles are user content and the tooltip is raw HTML, so they cannot go
// in unescaped — echarts only escapes for the formatter it generates itself.
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Every story on the chart is a series, so an axis tooltip lists all of them —
 * an account with 30 stories got 30 rows, nearly all reading 0, in whatever
 * order the series happened to be built. Nothing in that told a reader which
 * line the cursor was actually on. Keep only the stories read on that day,
 * strongest first, so a line's height maps to a named row.
 */
const formatTooltip = (params: TooltipParam[] | TooltipParam) => {
  const rows = Array.isArray(params) ? params : [params];
  if (!rows.length) return "";

  const date = escapeHtml(rows[0].axisValueLabel ?? "");
  const header = `<div style="margin-bottom:6px;font-weight:600">${date}</div>`;

  const read = rows
    .filter((row) => Number(row.value) > 0)
    .sort((a, b) => Number(b.value) - Number(a.value));

  // This chart renders the Sessions tab too, so the empty-day copy cannot name
  // readers specifically.
  if (!read.length)
    return `${header}<div style="opacity:0.7">No activity</div>`;

  const body = read
    .slice(0, MAX_TOOLTIP_ROWS)
    .map(
      (row) =>
        `<div style="display:flex;align-items:center;gap:8px;line-height:20px">` +
        `${row.marker ?? ""}` +
        `<span style="flex:1;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(row.seriesName ?? "")}</span>` +
        `<span style="font-weight:600">${Number(row.value)}</span>` +
        `</div>`,
    )
    .join("");

  const hidden = read.length - MAX_TOOLTIP_ROWS;
  const more =
    hidden > 0
      ? `<div style="margin-top:4px;opacity:0.7">+${hidden} more</div>`
      : "";

  return `${header}${body}${more}`;
};

const getLineChartOptions = (
  seriesList: ChartSeries[],
  dates: string[],
  forceShowGrid = false,
) => ({
  tooltip: {
    trigger: "axis",
    formatter: formatTooltip,
    backgroundColor: "#23272F",
    borderColor: "#23272F",
    borderWidth: 1,
    textStyle: {
      color: "#FFF",
      fontFamily: "Figtree",
      fontSize: 14,
      fontWeight: 500,
    },
    padding: 12,
    extraCssText: "box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 8px;",
    axisPointer: {
      type: "line",
      lineStyle: { color: "#C2C8D0", width: 1, type: "dashed" },
    },
  },
  legend: {
    top: 0,
    left: "center",
    type: "scroll",
    orient: "horizontal",
    textStyle: {
      color: "#12033E",
      fontFamily: "Figtree",
      fontSize: 11,
      fontWeight: 400,
    },
    itemWidth: 16,
    itemHeight: 8,
    icon: "path://M0,4 a4,4 0 0,1 4,-4 h6 a4,4 0 0,1 4,4 v0 a4,4 0 0,1 -4,4 h-6 a4,4 0 0,1 -4,-4 z",
    padding: 0,
    itemGap: 16,
  },
  grid: { left: 10, right: 20, top: 40, bottom: 0, containLabel: true },
  xAxis: {
    type: "category",
    data: dates,
    boundaryGap: false,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: "#88819F",
      fontFamily: "Figtree",
      fontSize: 10,
      fontWeight: 400,
      overflow: "truncate",
      ellipsis: true,
    },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: forceShowGrid ? 10 : undefined,
    splitLine: { show: true, lineStyle: { color: "#E5E7F0" } },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: "#88819F",
      fontFamily: "Figtree",
      fontSize: 10,
      fontWeight: 400,
      overflow: "truncate",
      ellipsis: true,
    },
  },
  series: seriesList.map((s) => ({
    name: s.name,
    type: "line",
    data: s.data,
    smooth: true,
    symbol: "circle",
    symbolSize: 12,
    itemStyle: { color: s.color, borderColor: "#fff", borderWidth: 3 },
    lineStyle: { color: s.color, width: 3 },
    emphasis: { focus: "series" },
    sampling: "lttb", // Performance: downsample for large datasets
  })),
});

export default getLineChartOptions;
